export enum DsmApsStatus {
  Cancelled = '989CC75C-CD70-4482-BD18-0591FA3D2F2F',
  Selection = '299EE95B-A733-45C9-B04B-10F01CE67C48',
  FinalSale = '44734B80-46C3-458C-948A-4FBFFD67FED2',
  ContractReady = '173F4025-4718-4A57-9F3F-5C70F9FF3225',
  PendingOffer = 'CCF17917-4F55-447B-8739-9708AEAD52E3',
  UnderContract = 'A1B2C3D4-E5F6-7890-ABCD-EF1234567890'
}

export const DsmApsStatusLabels: Record<DsmApsStatus, string> = {
  [DsmApsStatus.Cancelled]: 'Cancelled',
  [DsmApsStatus.Selection]: 'Selection',
  [DsmApsStatus.FinalSale]: 'Deal Closed',
  [DsmApsStatus.ContractReady]: 'Contract Ready',
  [DsmApsStatus.PendingOffer]: 'Pending Offer',
  [DsmApsStatus.UnderContract]: 'Under Contract'
};

export interface DsmApsStatusOption {
  id: string;
  firstLabel: string;
}

export interface DsmDealLite {
  contractDate: string;
  dealId: string;
  dealStatus: { id: string; name: string };
  elevation: { id: string; name: string };
  lot: { id: string; name: string };
  model: { id: string; name: string };
  project: { id: string; name: string };
  prospect: {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    email: string;
    phoneNumber: string;
  };
  salesOffice: { id: string; name: string };
  targetClosingDate: string | null;
  totalSignees: number;
  signedSignees: number;
  hasLocationMismatch?: boolean;
  hasPendingLocationRules: boolean;
}

export interface DsmDealLocationWarnings {
  dealId: string;
  locationMismatchAddendumId: string | null;
  locationMismatchOptionId: string | null;
  pendingLocationRulesAddendumId: string | null;
}

export interface DsmStatusCount {
  statusId: string;
  statusName: string;
  count: number;
}

export interface DsmDealsLiteResponse {
  deals: DsmDealLite[];
  counts: DsmStatusCount[];
}

export interface DsmProject {
  id?: string;
  projectId?: string;
  projectCode?: string;
  projectName?: string;
}

export interface DsmProjectLookup {
  id: string;
  projCode: string;
  projectName: string;
  productListId: string;
  currencyCode: string;
}
export interface DsmSalesOfficeOption {
  id: string;
  firstLabel: string;
  secondLabel?: number;
  currencyCode?: string;
}

export interface DsmSalesOfficeProjectCount {
  id: string;
  name: string;
  projectCount: number;
}

export interface DsmProjectOption {
  id: string;
  firstLabel: string;
  secondLabel: string;
}

// ── Overview ─────────────────────────────────────────────────────────────────

export interface DsmDealHeaderProject {
  id: string;
  projectCode: string;
  projectName?: string;
}

export interface DsmDealHeaderLotAddress {
  streetNo: string;
  street: string;
  street2?: string;
  suite?: string;
  cityName?: string;
  provCode?: string;
  postal: string;
}

export interface DsmDealHeaderLot {
  id: string;
  lotNo: string;
  lotTypeId?: string;
  address?: DsmDealHeaderLotAddress;
  floorplanFileLink?: string;
  swing?: string | null;
}

export interface DsmDealHeaderModel {
  id: string;
  modelName: string;
}

export interface DsmDealHeaderElevation {
  id: string;
  elevationCode: string;
  description: string;
  basePrice?: number;
}

export interface DsmDealHeaderStatus {
  id: string;
  name: string;
}

export interface DsmDealHeaderProspect {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  uploadedCards?: DsmUploadedCard[];
}

export interface DsmProspectDocumentFile {
  id: string;
  s3Key: string;
  directUrl: string;
}

export interface DsmUploadedCard {
  id: string;
  frontS3DocumentId: string;
  frontDocument: DsmProspectDocumentFile;
  backS3DocumentId: string | null;
  backDocument: DsmProspectDocumentFile | null;
  documentTypeId: string;
  documentTypeName: string;
}

export interface DsmDealHeaderSalesAgent {
  id: string;
  name: string;
}

export interface DsmDealHeaderSalesFloorAgent {
  userId: string;
  firstName: string;
  lastName: string;
}

export interface DsmDealHeader {
  id: string;
  project?: DsmDealHeaderProject;
  lot?: DsmDealHeaderLot;
  model?: DsmDealHeaderModel;
  elevation?: DsmDealHeaderElevation;
  dealStatus?: DsmDealHeaderStatus;
  salesAgents: DsmDealHeaderSalesAgent[];
  salesFloorAgent?: DsmDealHeaderSalesFloorAgent;
  prospect?: DsmDealHeaderProspect;
  isCFL: boolean;
  hasActiveSigningContract: boolean;
}

export interface DsmBuyerSelections {
  contractId: string;
  dealId: string;
  unitPremiumPrice: number;
  baseModelPrice: number;
  inContractOptions: number;
  pendingOptions: number;
  totalOptions: number;
  total: number;
}

export interface DsmBuyerContracts {
  contractId: string;
  dealId: string;
  contractDate?: string;
  earnestDeposit: number;
  finalSaleDate?: string;
  approvalDate?: string;
  targetClosingDate?: string;
  actualClosingDate?: string;
  irrevocableDate?: string;
  eligibleForGSTRebate: boolean;
  investmentProperty: boolean;
}

export interface DsmSpecLotModel {
  id: string;
  projectModelId: string;
  modelName: string;
}

export interface DsmSpecLotSelectedModel {
  id: string;
  modelName: string;
}

export interface DsmSpecLotSelectedElevation {
  id: string;
  elevationCode: string;
  description: string;
}

export interface DsmSpecLotAddress {
  streetNo: string;
  street: string;
  street2: string | null;
  suite: string | null;
  cityId: string;
  postal: string;
  cityName: string;
}

export interface DsmSpecLot {
  id: string;
  projectId: string;
  lotNo: string;
  lotTypeName: string;
  lotTypeId?: string;
  status: string | null;
  statusName: string | null;
  address: DsmSpecLotAddress | null;
  models: DsmSpecLotModel[];
  selectedModel: DsmSpecLotSelectedModel | null;
  selectedElevation: DsmSpecLotSelectedElevation | null;
  finalSaleDate: string | null;
  legalUnit: string | null;
  projectCode?: string;
  lotPremiumAmount?: number | null;
  lotUnitPrice?: number | null;
  releaseToConstruction?: boolean | null;
  swing?: string | null;
}

export interface DsmSpecLotElevationOption {
  id: string;
  elevationCode: string;
  description: string | null;
}

export interface DsmSpecLotModelOption {
  modelId: string;
  modelName: string;
  elevations: DsmSpecLotElevationOption[];
}
